import { ReactNode, useEffect, useState } from 'react';
import { IStudy } from '@vivli/features/studies/infrastructure/interface';

import {
    useActiveUserContext,
    useAssignedAppTypeContext,
    useDataRequestsApiContext,
    useModalApiContext,
} from '@vivli/core/infrastructure/context';
import { useRequestedStudyPermissions } from '@vivli/features/studies/infrastructure/hook';
import { IDataRequest, IRequestedStudy } from '@vivli/features/data-requests/infrastructure/interface';
import { first } from 'rxjs/operators';
import { DatasetContext } from '@vivli/features/datasets/infrastructure/context';

interface DatasetContextWrapperProps {
    children: ReactNode;
    study: IStudy;
    requestedStudyDownloadableIpdPackageId: string;
    isLanding: boolean;
    dataRequestId?: string;
}

export const DatasetContextWrapper = ({
    children,
    study,
    requestedStudyDownloadableIpdPackageId,
    isLanding,
    dataRequestId,
}: DatasetContextWrapperProps) => {
    const [showDownload, setShowDownload] = useState<boolean>(false);
    const [requestedStudy, setRequestedStudy] = useState<IRequestedStudy>(null);
    const dataRequestsService = useDataRequestsApiContext();
    const modal = useModalApiContext();
    const assignedAppType = useAssignedAppTypeContext();
    const { user } = useActiveUserContext();
    const { userCanDownloadIpd } = useRequestedStudyPermissions();

    const getRequestedStudy = (dataRequest: IDataRequest) => {
        return dataRequest?.requestedStudies?.find((rs) => rs.studyId === study.id);
    };

    const handleError = (error: string) => {
        modal.error(error);
    };

    const provider = {
        study: study,
        requestedStudy: requestedStudy,
        requestedStudyDownloadableIpdPackageId: requestedStudyDownloadableIpdPackageId,
        showDownload: showDownload,
        user: user,
        assignedAppType: assignedAppType.type,
        isLanding: isLanding,
    };

    useEffect(() => {
        //note we deliberately don't require requestedStudyDownloadableIpdPackageId here -
        //a requested study with no data package yet still needs to be available to
        //consumers (e.g. the admin details downloadable setting). The download button
        //itself is still gated on the package id, below.
        if (dataRequestId && study) {
            dataRequestsService
                .getDataRequest(dataRequestId)
                .pipe(first())
                .subscribe((dr) => {
                    const reqStudy = getRequestedStudy(dr);
                    setRequestedStudy(reqStudy);

                    if (requestedStudyDownloadableIpdPackageId) {
                        const downloadable = userCanDownloadIpd(reqStudy, dr);
                        setShowDownload(downloadable);
                    }
                }, handleError);
        }
    }, [requestedStudyDownloadableIpdPackageId, dataRequestId, study]);

    return <DatasetContext.Provider value={provider}>{children}</DatasetContext.Provider>;
};
